import { useRef, useState, type ReactNode } from "react"
import {
  transactionQuestionQuickAnswers,
  type PortalClientFile,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import { formatShortDate } from "./format"
import { isSentBack } from "./item-state"
import {
  questionAmount,
  questionNoteLabel,
  questionView,
  visibleQuestionRows,
} from "./transaction-question-model"
import { HiddenFileInput, InlineError } from "./ui"

type RequestItem = PortalRequestResponse["items"][number]

/**
 * A question about transactions: the rows the firm could not put a category
 * on, and the client's answer. The client picks one of the firm's categories
 * or tells the firm what the rows were, and can add a receipt. The answer
 * waits for the firm; nothing changes in the books until they accept it.
 * The checklist mounts it for items of this kind.
 */
export function TransactionQuestion({
  item,
  categoryChoices,
  firmName,
  files,
  uploadAllowed,
  onFiles,
  onAnswered,
  filePills = true,
  heading = true,
  showSentBack = false,
  thread,
}: {
  item: RequestItem
  categoryChoices: readonly { id: string; name: string }[]
  firmName: string
  /** Receipts already sent on this item. */
  files: PortalClientFile[]
  uploadAllowed: boolean
  onFiles(files: FileList): void
  onAnswered(request: PortalRequestResponse): void
  /** The receipts by name. The engagement task lists them itself, with the
      state of each, so it turns these off. */
  filePills?: boolean
  /** The question's name and its tick. The engagement task row above an
      open item already shows both, so it turns them off. */
  heading?: boolean
  /** The question's own row says the firm asked again, with their reason;
      the engagement row above it says it there instead. */
  showSentBack?: boolean
  /** The conversation about the item: "Ask again" writes the firm's note
      there. */
  thread?: ReactNode
}) {
  const api = usePortalApi()
  const view = questionView({ item, categoryChoices, firmName })
  const [changing, setChanging] = useState(false)
  const [picking, setPicking] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [categoryId, setCategoryId] = useState(item.answer?.categoryId ?? "")
  const [note, setNote] = useState(
    typeof item.answer?.value === "string" ? item.answer.value : ""
  )
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const rows = visibleQuestionRows(item.question?.rows ?? [], showAll)
  const done = view.kind === "done"
  const asking = view.kind === "ask" || changing

  const save = async (answer: { note: string; categoryId: string | null }) => {
    setPending(true)
    setError(null)
    try {
      onAnswered(
        await api.answerItem(item.itemId, answer.note, answer.categoryId)
      )
      setChanging(false)
      setPicking(false)
    } catch (answerError) {
      setError(
        errorMessage(
          answerError instanceof Error
            ? answerError
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }

  const noteLabel = questionNoteLabel(item, firmName)
  const suggestion = view.kind === "ask" ? view.suggestion : null
  const showPicker = asking && (picking || changing || !suggestion)

  return (
    <article
      id={`ask-${item.itemId}`}
      className="ask-item question-item"
      role="group"
      aria-label={item.name}
      tabIndex={-1}
    >
      {heading ? (
        <span
          className={`tick${view.kind === "ask" ? "" : " is-in"}`}
          aria-hidden="true"
        />
      ) : null}
      <div className="ask-main">
        {heading ? <b>{item.name}</b> : null}
        {showSentBack && isSentBack(item) ? (
          <span className="sent-back">
            <b>Sent back</b>
            <span>{item.rejectionReason}</span>
          </span>
        ) : null}
        <span className="d">
          {view.kind === "ask" ? view.prompt : view.text}
        </span>

        <ul className="question-rows">
          {rows.rows.map((row) => (
            <li key={`${row.recordType}:${row.recordIndex}`}>
              <span className="mono">
                {row.recordDate ? formatShortDate(row.recordDate) : ""}
              </span>
              <span className="question-row-description">
                {row.description ?? "No description"}
              </span>
              <span className="mono">
                {questionAmount(row.amount, row.currency)}
              </span>
            </li>
          ))}
        </ul>
        {rows.more ? (
          <button
            type="button"
            className="text-button question-more"
            onClick={() => setShowAll(true)}
          >
            {rows.more}
          </button>
        ) : null}

        {view.kind === "answered" && !changing ? (
          <button
            type="button"
            className="text-button question-change"
            onClick={() => setChanging(true)}
          >
            Change
          </button>
        ) : null}

        {asking && suggestion && !showPicker ? (
          <div
            className="answer-pills"
            role="group"
            aria-label={`Is this ${suggestion.name}?`}
          >
            <span className="question-suggestion">{`Is this ${suggestion.name}?`}</span>
            <button
              type="button"
              className="choice-pill"
              disabled={pending}
              onClick={() => void save({ note: "", categoryId: suggestion.id })}
            >
              Yes
            </button>
            <button
              type="button"
              className="choice-pill"
              disabled={pending}
              onClick={() => setPicking(true)}
            >
              No
            </button>
          </div>
        ) : null}

        {showPicker ? (
          <form
            className="question-picker"
            onSubmit={(event) => {
              event.preventDefault()
              void save({ note, categoryId: categoryId || null })
            }}
          >
            <select
              aria-label="Pick a category"
              value={categoryId}
              disabled={pending}
              onChange={(event) => setCategoryId(event.currentTarget.value)}
            >
              <option value="">Pick a category</option>
              {categoryChoices.map((choice) => (
                <option key={choice.id} value={choice.id}>
                  {choice.name}
                </option>
              ))}
            </select>
            <textarea
              aria-label={noteLabel}
              placeholder={noteLabel}
              maxLength={2000}
              rows={2}
              value={note}
              disabled={pending}
              onChange={(event) => setNote(event.currentTarget.value)}
            />
            <div className="question-actions">
              <button
                type="submit"
                className="ghost-pill"
                disabled={pending || (!categoryId && !note.trim())}
              >
                {pending ? "Saving…" : "Save answer"}
              </button>
              {transactionQuestionQuickAnswers.map((quick) => (
                <button
                  key={quick}
                  type="button"
                  className="text-button"
                  disabled={pending}
                  onClick={() => void save({ note: quick, categoryId: null })}
                >
                  {quick}
                </button>
              ))}
            </div>
          </form>
        ) : null}

        {filePills && files.length > 0 ? (
          <div className="file-pills">
            {files.map((file) => (
              <span
                className="file-pill mono"
                key={file.fileId}
                title={file.fileName}
              >
                {file.fileName}
              </span>
            ))}
          </div>
        ) : null}
        <HiddenFileInput ref={fileInput} multiple onFiles={onFiles} />
        {error ? <InlineError message={error} /> : null}
        {thread}
      </div>
      {uploadAllowed && !done ? (
        <div className="ask-action">
          <button
            type="button"
            className="ghost-pill"
            onClick={() => fileInput.current?.click()}
          >
            Add a receipt
          </button>
        </div>
      ) : null}
    </article>
  )
}
